import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../../utils/apiFetch";
import apiPath from "../../../utils/pathObj";
import dayjs from "dayjs";

const Subscription = () => {
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [originalEmployeeCount, setOriginalEmployeeCount] = useState(0);
  const [additionalEmployees, setAdditionalEmployees] = useState(0);
  const [pricePerEmployee, setPricePerEmployee] = useState(0);
  const [additionalAmount, setAdditionalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [jobBreakdown, setJobBreakdown] = useState(null);
  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false);

  const getSubscriptionList = async () => {
    try {
      const res = await apiGet(apiPath.getSubscription, {});
      const data = res?.data?.results;
      setSubscriptionData(data);

      const originalCount = data?.noOfEmployee || 0;
      const additional = data?.additionalEmployees || 0;

      setOriginalEmployeeCount(originalCount);
      setAdditionalEmployees(additional);
      setEmployeeCount(originalCount + additional);

      if (data?.subscriptionAmount && data?.noOfEmployee) {
        const perEmployeePrice = Math.round(data.subscriptionAmount / data.noOfEmployee);
        setPricePerEmployee(perEmployeePrice);
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  useEffect(() => {
    const fetchBreakdown = async () => {
      try {
        setIsLoadingBreakdown(true);
        const res = await apiGet(apiPath.getJobBillingBreakdown);
        if (res?.data?.success) {
          setJobBreakdown(res.data.results);
        }
      } catch (e) {
        console.error("Breakdown fetch failed", e);
      } finally {
        setIsLoadingBreakdown(false);
      }
    };
    fetchBreakdown();
  }, []);

  useEffect(() => {
    getSubscriptionList();
  }, []);

  useEffect(() => {
    const currentTotal = originalEmployeeCount + additionalEmployees;
    if (employeeCount > currentTotal) {
      const newAdditionalEmployees = employeeCount - currentTotal;
      setAdditionalAmount(newAdditionalEmployees * pricePerEmployee);
    } else {
      setAdditionalAmount(0);
    }
  }, [employeeCount, originalEmployeeCount, additionalEmployees, pricePerEmployee]);

  const handleIncrement = () => {
    setEmployeeCount(prev => prev + 1);
  };

  const handleDecrement = () => {
    const currentTotal = originalEmployeeCount + additionalEmployees;
    if (employeeCount > currentTotal) {
      setEmployeeCount(prev => prev - 1);
    }
  };

  const handleUpdateSubscription = async () => {
    if (additionalAmount <= 0) return;

    setLoading(true);
    try {
      const response = await apiPost(apiPath.updateSubscriptionEmployees, {
        subscriptionId: subscriptionData._id,
        additionalEmployees: employeeCount - (originalEmployeeCount + additionalEmployees),
        additionalAmount: additionalAmount,
        totalEmployees: employeeCount
      });

      if (response?.data?.results?.sessionUrl) {
        window.location.href = response.data.results.sessionUrl;
      }
    } catch (error) {
      console.log("error:", error);
      alert("Failed to process request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-3 py-4">
      <div className="bg-white border border-[#E9EDF9] rounded-lg py-4 ">
        <div className="relative p-6 flex-auto">
          <div className="grid grid-cols-4 gap-4">
            <div className="sm:py-4 sm:px-2 py-8 px-7">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Subscription title</strong>
                <h6>{subscriptionData.subscriptionName || ""}</h6>
              </div>
            </div>
            <div className="sm:py-4 sm:px-2 py-8 px-7">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Subscription price</strong>
                <h6>${subscriptionData.subscriptionAmount + subscriptionData.additionalAmountPaid || 0}</h6>
              </div>
            </div>

            <div className="sm:py-4 sm:px-2 py-8 px-7">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Job posting amount</strong>
                <h6>${subscriptionData.jobPostingAmount || 0}</h6>
                <p className="text-xs text-gray-500 mt-1">Billed this cycle</p>
              </div>
            </div>

            <div className="sm:py-4 sm:px-2 py-8 px-7">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Total amount</strong>
                <h6>
                  $
                  {(subscriptionData.subscriptionAmount || 0) +
                    (subscriptionData.additionalAmountPaid || 0) +
                    (subscriptionData.jobPostingAmount || 0)}
                </h6>
              </div>
            </div>

            <div className="sm:py-4 sm:px-2 py-8 px-7">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Number of HRs</strong>
                <h6>{subscriptionData.noOfHR || ""}</h6>
              </div>
            </div>
            <div className="sm:py-4 sm:px-2 py-8 px-7">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Number of accountants</strong>
                <h6>{subscriptionData.noOfAccountant || ""}</h6>
              </div>
            </div>

            <div className="sm:py-4 sm:px-2 py-8 px-7 col-span-1">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Number of employees</strong>
                <div className="flex items-center mt-2 space-x-2">
                  <button
                    onClick={handleDecrement}
                    className={`w-6 h-6 rounded-full border ${
                      employeeCount <= (originalEmployeeCount + additionalEmployees)
                        ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                        : 'border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white'
                    } transition-colors duration-200`}
                    disabled={employeeCount <= (originalEmployeeCount + additionalEmployees)}
                  >
                    -
                  </button>
                  <span className="text-xl font-semibold min-w-[50px] text-center">
                    {employeeCount}
                  </span>
                  <button
                    onClick={handleIncrement}
                    className="w-6 h-6 rounded-full border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white transition-colors duration-200"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {additionalAmount > 0 && (
              <div className="sm:py-4 sm:px-6 py-3 px-7 col-span-2 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}>
                <div className="relative z-0 w-full group">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="" style={{ fontSize: "18px", opacity: 0.9 }}>Additional amount to pay:</span>
                        <h6 className="text-2xl font-bold">${additionalAmount}</h6>
                      </div>
                      <p className="text-sm mt-1" style={{ opacity: 0.7 }}>
                        For {employeeCount - (originalEmployeeCount + additionalEmployees)} new additional employee(s) at ${pricePerEmployee} each
                      </p>
                    </div>
                    <button
                      onClick={handleUpdateSubscription}
                      disabled={loading}
                      className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${
                        loading
                          ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500'
                          : 'hover:shadow-lg hover:scale-105'
                      }`}
                      style={{
                        backgroundColor: loading ? '' : '#2E3284',
                        color: loading ? '' : 'white'
                      }}
                    >
                      {loading ? 'Processing...' : 'Pay Now'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="sm:py-4 sm:px-2 py-8 px-7">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Purchased date</strong>
                <h6>{dayjs(subscriptionData.subscriptionPurchaseDate).format("MM-DD-YYYY hh:mm A")} </h6>
              </div>
            </div>
            <div className="sm:py-4 sm:px-2 py-8 px-7">
              <div className="relative z-0 mb-2 w-full group">
                <strong>Expiry date</strong>
                <h6>{dayjs(subscriptionData.subscriptionExpiryDate).format("MM-DD-YYYY hh:mm A")} </h6>
              </div>
            </div>
          </div>

          {/* Job posting breakdown */}
          {jobBreakdown && (
            <div className="mt-6 border-t pt-4">
              <h3 className="text-lg font-semibold mb-3">Job posting breakdown</h3>

              {/* Active jobs — currently billed */}
              {jobBreakdown.contributing?.length > 0 ? (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">
                    Active jobs — billed this cycle
                    {jobBreakdown.billingCycleStart && jobBreakdown.billingCycleEnd && (
                      <> ({dayjs(jobBreakdown.billingCycleStart).format("MMM D")} – {dayjs(jobBreakdown.billingCycleEnd).format("MMM D, YYYY")})</>
                    )}
                  </p>
                  <div className="space-y-2">
                    {jobBreakdown.contributing.map((j) => (
                      <div
                        key={j.jobId}
                        className="flex items-center justify-between bg-blue-50 p-3 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{j.name}</p>
                          <p className="text-xs text-gray-500">
                            ${j.budget} / {j.duration} days · ${j.dailyRate.toFixed(2)}/day
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-blue-700">
                            ${j.chargedThisCycle.toFixed(2)}
                          </p>
                          {j.deferredAmount > 0 && (
                            <p className="text-xs text-gray-500">
                              + ${j.deferredAmount.toFixed(2)} next cycle
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic mb-4">
                  No active jobs are contributing to this cycle.
                </div>
              )}

              {/* Inactive jobs — no longer billed */}
              {jobBreakdown.inactive?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">
                    Inactive jobs — no longer billed ({jobBreakdown.inactive.length})
                  </p>
                  <div className="space-y-2">
                    {jobBreakdown.inactive.map((j) => (
                      <div
                        key={j.jobId}
                        className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-600">{j.name}</p>
                          <p className="text-xs text-gray-400">
                            ${j.budget} / {j.duration} days · status {j.status}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">$0.00</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Upgrade History */}
          {subscriptionData?.upgradeHistory?.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h3 className="text-lg font-semibold mb-3">Upgrade History</h3>
              <div className="space-y-2">
                {subscriptionData.upgradeHistory.map((upgrade, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm">
                      <span className="font-medium">Added {upgrade.additionalEmployees} employee(s)</span> -
                      Paid ${upgrade.amountPaid} on {dayjs(upgrade.upgradedAt).format("MM-DD-YYYY")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscription;





// import React, { useState, useEffect } from "react";
// import { apiGet, apiPost } from "../../../utils/apiFetch";
// import apiPath from "../../../utils/pathObj";
// import dayjs from "dayjs";

// const Subscription = () => {
//   const [subscriptionData, setSubscriptionData] = useState([]);
//   const [employeeCount, setEmployeeCount] = useState(0);
//   const [originalEmployeeCount, setOriginalEmployeeCount] = useState(0);
//   const [additionalEmployees, setAdditionalEmployees] = useState(0);
//   const [pricePerEmployee, setPricePerEmployee] = useState(0);
//   const [additionalAmount, setAdditionalAmount] = useState(0);
//   const [loading, setLoading] = useState(false);

//   const getSubscriptionList = async () => {
//     try {
//       const res = await apiGet(apiPath.getSubscription, {});
//       const data = res?.data?.results;
//       setSubscriptionData(data);
      
//       // Set initial employee counts
//       const originalCount = data?.noOfEmployee || 0;
//       const additional = data?.additionalEmployees || 0;
      
//       setOriginalEmployeeCount(originalCount);
//       setAdditionalEmployees(additional);
//       setEmployeeCount(originalCount + additional);
      
//       // Calculate price per employee and round to nearest integer
//       if (data?.subscriptionAmount && data?.noOfEmployee) {
//         const perEmployeePrice = Math.round(data.subscriptionAmount / data.noOfEmployee);
//         setPricePerEmployee(perEmployeePrice);
//       }
//     } catch (error) {
//       console.log("error:", error);
//     }
//   };

//   useEffect(() => {
//     getSubscriptionList();
//   }, []);

//   // Calculate additional amount for new additional employees
//   useEffect(() => {
//     const currentTotal = originalEmployeeCount + additionalEmployees;
//     if (employeeCount > currentTotal) {
//       const newAdditionalEmployees = employeeCount - currentTotal;
//       setAdditionalAmount(newAdditionalEmployees * pricePerEmployee);
//     } else {
//       setAdditionalAmount(0);
//     }
//   }, [employeeCount, originalEmployeeCount, additionalEmployees, pricePerEmployee]);

//   const handleIncrement = () => {
//     setEmployeeCount(prev => prev + 1);
//   };

//   const handleDecrement = () => {
//     const currentTotal = originalEmployeeCount + additionalEmployees;
//     if (employeeCount > currentTotal) {
//       setEmployeeCount(prev => prev - 1);
//     }
//   };

//   const handleUpdateSubscription = async () => {
//     if (additionalAmount <= 0) return;
    
//     setLoading(true);
//     try {
//       const response = await apiPost(apiPath.updateSubscriptionEmployees, {
//         subscriptionId: subscriptionData._id,
//         additionalEmployees: employeeCount - (originalEmployeeCount + additionalEmployees),
//         additionalAmount: additionalAmount,
//         totalEmployees: employeeCount
//       });

//       if (response?.data?.results?.sessionUrl) {
//         // Redirect to Stripe payment page
//         window.location.href = response.data.results.sessionUrl;
//       }
//     } catch (error) {
//       console.log("error:", error);
//       alert("Failed to process request. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="px-3 py-4">
//       <div className="bg-white border border-[#E9EDF9] rounded-lg py-4 ">
//         <div className="relative p-6 flex-auto">
//           <div className="grid grid-cols-4 gap-4">
//             <div className="sm:py-4 sm:px-2 py-8 px-7">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Subscription title</strong>
//                 <h6>{subscriptionData.subscriptionName || ""}</h6>
//               </div>
//             </div>
//             <div className="sm:py-4 sm:px-2 py-8 px-7">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Subscription price</strong>
//                 <h6>${subscriptionData.subscriptionAmount + subscriptionData.additionalAmountPaid || 0}</h6>
//               </div>
//             </div>

//             {/* NEW: Job posting amount — separate from employee amounts.
//                 This is already the MONTHLY-BILLED total (each job's
//                 budget is scaled by its duration on the backend before
//                 being added here), not a raw sum of job budgets. */}
//             <div className="sm:py-4 sm:px-2 py-8 px-7">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Job posting amount</strong>
//                 <h6>${subscriptionData.jobPostingAmount || 0}</h6>
//                 <p className="text-xs text-gray-500 mt-1">Billed monthly</p>
//               </div>
//             </div>

//             {/* NEW: Combined total across base subscription, employee amount, and job posting amount */}
//             <div className="sm:py-4 sm:px-2 py-8 px-7">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Total amount</strong>
//                 <h6>
//                   $
//                   {(subscriptionData.subscriptionAmount || 0) +
//                     (subscriptionData.additionalAmountPaid || 0) +
//                     (subscriptionData.jobPostingAmount || 0)}
//                 </h6>
//               </div>
//             </div>

//             <div className="sm:py-4 sm:px-2 py-8 px-7">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Number of HRs</strong>
//                 <h6>{subscriptionData.noOfHR || ""}</h6>
//               </div>
//             </div>
//             <div className="sm:py-4 sm:px-2 py-8 px-7">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Number of accountants</strong>
//                 <h6>{subscriptionData.noOfAccountant || ""}</h6>
//               </div>
//             </div>
            
//             {/* Employee Count with Increment/Decrement */}
//             <div className="sm:py-4 sm:px-2 py-8 px-7 col-span-1">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Number of employees</strong>
//                 <div className="flex items-center mt-2 space-x-2">
//                   <button
//                     onClick={handleDecrement}
//                     className={`w-6 h-6 rounded-full border ${
//                       employeeCount <= (originalEmployeeCount + additionalEmployees)
//                         ? 'border-gray-300 text-gray-300 cursor-not-allowed'
//                         : 'border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white'
//                     } transition-colors duration-200`}
//                     disabled={employeeCount <= (originalEmployeeCount + additionalEmployees)}
//                   >
//                     -
//                   </button>
//                   <span className="text-xl font-semibold min-w-[50px] text-center">
//                     {employeeCount}
//                   </span>
//                   <button
//                     onClick={handleIncrement}
//                     className="w-6 h-6 rounded-full border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white transition-colors duration-200"
//                   >
//                     +
//                   </button>
//                 </div>
//                 {/* <div className="mt-2 text-sm">
//                   <p className="text-gray-600">Base employees: {originalEmployeeCount}</p>
//                   {additionalEmployees > 0 && (
//                     <p className="text-green-600">Previously added: +{additionalEmployees}</p>
//                   )}
//                 </div> */}
//               </div>
//             </div>

//             {/* Additional Amount Display */}
//             {additionalAmount > 0 && (
//               <div className="sm:py-4 sm:px-6 py-3 px-7 col-span-2 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}>
//                 <div className="relative z-0 w-full group">
//                   <div className="flex items-center justify-between flex-wrap gap-4">
//                     <div>
//                       <div className="flex items-baseline gap-2">
//                         <span className="" style={{fontSize:"18px", opacity: 0.9}}>Additional amount to pay:</span>
//                         <h6 className="text-2xl font-bold">${additionalAmount}</h6>
//                       </div>
//                       <p className="text-sm mt-1" style={{ opacity: 0.7 }}>
//                         For {employeeCount - (originalEmployeeCount + additionalEmployees)} new additional employee(s) at ${pricePerEmployee} each
//                       </p>
//                     </div>
//                     <button
//                       onClick={handleUpdateSubscription}
//                       disabled={loading}
//                       className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${
//                         loading 
//                           ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500' 
//                           : 'hover:shadow-lg hover:scale-105'
//                       }`}
//                       style={{ 
//                         backgroundColor: loading ? '' : '#2E3284',
//                         color: loading ? '' : 'white'
//                       }}
//                     >
//                       {loading ? 'Processing...' : 'Pay Now'}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             )}

//             <div className="sm:py-4 sm:px-2 py-8 px-7">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Purchased date</strong>
//                 <h6>{dayjs(subscriptionData.subscriptionPurchaseDate).format("MM-DD-YYYY hh:mm A")} </h6>
//               </div>
//             </div>
//             <div className="sm:py-4 sm:px-2 py-8 px-7">
//               <div className="relative z-0 mb-2 w-full group">
//                 <strong>Expiry date</strong>
//                 <h6>{dayjs(subscriptionData.subscriptionExpiryDate).format("MM-DD-YYYY hh:mm A")} </h6>
//               </div>
//             </div>
//           </div>

//           {/* Upgrade History */}
//           {subscriptionData?.upgradeHistory?.length > 0 && (
//             <div className="mt-6 border-t pt-4">
//               <h3 className="text-lg font-semibold mb-3">Upgrade History</h3>
//               <div className="space-y-2">
//                 {subscriptionData.upgradeHistory.map((upgrade, index) => (
//                   <div key={index} className="bg-gray-50 p-3 rounded-lg">
//                     <p className="text-sm">
//                       <span className="font-medium">Added {upgrade.additionalEmployees} employee(s)</span> - 
//                       Paid ${upgrade.amountPaid} on {dayjs(upgrade.upgradedAt).format("MM-DD-YYYY")}
//                     </p>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Subscription;